import { serialize } from 'object-to-formdata';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { CustomInput, File } from '~/components/common/forms';
import { Translate, useTranslate } from '~/i18n';

import Axios from '~/utils/axios';
import { BankDetailsList, BranchDetailsList } from './data';
import { Button } from '~/components/common/button';
import { CopyIcon, CloseIcon } from '~/svgs';
import InputMask from 'react-input-mask';
import Modal from '~/components/common/modal';
import { Section } from '~/components/common/section';
import { WalletOutlineIcon } from '~/svgs/marketplace';
import { sentryCaptureException } from '~/errors/sentry';
import { successMessage } from '~/utils/toasts';
import useCopyToClipboard from '~/hooks/use-copy-to-clipboard';
import { WalletTerms } from './wallet-terms';

type ChargeWalletModalType = {
   open: boolean;
   setOpen: (value: boolean) => void;
};

type AddBalanceProps = {
   amount: number;
   receipt: File | string;
};

const ChargeWalletModal = ({ open, setOpen }: ChargeWalletModalType) => {
   const { translate } = useTranslate();
   const [loading, setLoading] = useState(false);
   const [isChargeRequestDone, setIsChargeRequestDone] = useState(false);
   const [termsAccepted, setTermsAccepted] = useState(false);
   const [showTerms, setShowTerms] = useState(false);

   const {
      register,
      control,
      reset,
      setError,
      formState: { errors },
      handleSubmit,
   } = useForm<AddBalanceProps>();

   const submitForm = async (values: any) => {
      const data = { ...values };
      if (data.receipt) data.receipt = data.receipt[0];

      try {
         setLoading(true);
         const form = serialize(data);
         // form.append('_method', 'PATCH');
         const { data: response } = await Axios.post(`/hy-providers/v1/wallet/add_credit`, form);
         setLoading(false);
         if (response?.status === 'Success') {
            setIsChargeRequestDone(true);
            setTermsAccepted(false);
            reset();
         } else {
            throw new Error(data?.error || data?.original?.error || 'Something went wrong');
         }
      } catch (error: any) {
         sentryCaptureException(error, {
            level: 'fatal',
            tags: { journey: 'business' },
         });
         if (error.response?.status === 422) {
            // Looping errors coming from backend (should be inside error)
            Object.entries(error.response?.data?.errors).map(([key, value], idx) => {
               setError(key as any, { message: value + '' }, { shouldFocus: idx === 0 });
            });
            setLoading(false);
         }
      }
   };

   return (
      <Modal
         size={isChargeRequestDone ? 'sm' : 'lg'}
         open={open}
         onClickOutside={() => {
            setOpen(false);
            setTermsAccepted(false);
            reset();
            setIsChargeRequestDone(false);
         }}>
         <Section>
            {showTerms && !isChargeRequestDone ? (
               <WalletTerms setTerms={setShowTerms} />
            ) : isChargeRequestDone ? (
               <RequestDoneAlert
                  closeModal={() => {
                     setOpen(false);
                     setIsChargeRequestDone(false);
                  }}
               />
            ) : (
               <form onSubmit={handleSubmit(submitForm)} className="w-full ">
                  <div className="flex px-8 py-6 font-bold border-b border-gray-300">
                     <Translate id="wallet:charge your wallet" />
                  </div>
                  <div className="p-8">
                     <div className="mb-1 font-semibold">
                        <Translate id="wallet:make a bank transfer" />
                     </div>
                     <div className="mb-6 text-sm">
                        <Translate id="wallet:make a bank transfer description" />
                     </div>
                     <div className="flex flex-col justify-center w-full border border-gray-300 rounded-md lg:flex-row">
                        <div className="flex flex-col w-full px-8 py-6 lg:w-1/2">
                           <Controller
                              name="amount"
                              control={control}
                              rules={{
                                 required: translate({ id: 'validation:required' }),
                              }}
                              render={({ field: { onBlur, onChange, ref, value } }) => (
                                 <InputMask
                                    mask="99999999"
                                    maskChar={null}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    defaultValue={value}>
                                    {(props: any) => (
                                       <CustomInput
                                          {...props}
                                          id="amount"
                                          className="ltr:!border-r-0 rtl:!border-l-0 !text-xs focus:!ring-transparent "
                                          label={
                                             <Translate id="wallet:enter amount you want to charge" />
                                          }
                                          labelClassName="font-semibold !text-sm"
                                          inputWrapperClassName="group"
                                          appendClassName={`!bg-white group-hover:border-primary  ${
                                             errors.amount
                                                ? '!border-red-500 group-focus:outline-red-500'
                                                : ''
                                          }`}
                                          append={
                                             <span className="mr-3 rtl:ml-3">
                                                <Translate id="common:sar" />
                                             </span>
                                          }
                                          defaultValue={value}
                                          error={errors.amount?.message}
                                          ref={ref}
                                       />
                                    )}
                                 </InputMask>
                              )}
                           />
                           <File
                              id="receipt"
                              label={<Translate id="wallet:upload proof" />}
                              labelClassName="font-semibold !text-sm mt-4"
                              {...register('receipt', {
                                 required: translate({ id: 'validation:required' }),
                              })}
                              error={(errors.receipt as any)?.message}
                              hasInlineThumbnail
                           />
                           <span className="text-xs text-gray-500">
                              <Translate id="wallet:upload proof description" />
                           </span>
                        </div>
                        <div className="flex flex-col w-full h-full col-span-2 px-8 py-6 space-y-6 lg:w-1/2">
                           <BankDetails />
                        </div>
                     </div>
                  </div>
                  <div className="flex items-center justify-end w-full gap-4 px-8 py-3 border-t border-gray-300">
                     <div className="flex gap-1">
                        <div>
                           <input
                              onClick={() => setTermsAccepted(!termsAccepted)}
                              type="checkbox"
                              defaultChecked={termsAccepted}
                              className="border-gray-200 rounded-sm text-primary"
                           />
                        </div>
                        <span
                           className="mx-1 cursor-pointer text-primary"
                           onClick={() => setShowTerms(true)}>
                           <Translate id="wallet:i agree to the terms" />
                        </span>
                     </div>

                     <Button
                        type="submit"
                        theme="primary"
                        disabled={loading || !termsAccepted || !!errors.amount || !!errors.receipt}
                        loading={loading}
                        className="w-32 h-10 !font-normal !px-10">
                        <Translate id="common:submit" />
                     </Button>
                  </div>
               </form>
            )}
         </Section>
      </Modal>
   );
};

// const ChargeSteps = () => {
//    return (
//       <div className="space-y-10">
//          {Steps.map(({ id, title, description }, index) => (
//             <div className="flex w-full gap-x-6" key={index}>
//                <div
//                   className={`w-5 h-5 mt-1 flex flex-shrink-0 ${
//                      id === Steps.length && 'before:content-none'
//                   } border border-dashed border-primary items-center text-center justify-center bg-[rgba(63,195,195,0.15)] text-xs text-primary relative font-bold rounded-full before:flex before:absolute before:m-auto before:min-h-[75px] before:w-[1px] before:h-full before:left-0 before:right-0 before:top-[20px] before:bg-transparent before:border-l before:border-primary before:border-dashed`}>
//                   {id}
//                </div>
//                <div className="flex flex-col space-y-1">
//                   <div className="text-xs font-semibold">{title}</div>
//                   <div className="text-xs text-[#828385]">{description}</div>
//                </div>
//             </div>
//          ))}
//       </div>
//    );
// };

const BankDetails = () => {
   const [value, copy] = useCopyToClipboard();
   const { translate } = useTranslate();

   const handleIsCopied = (text: string | Element | any) => {
      copy(text);
      if (value) {
         successMessage(translate({ id: 'common:copied to clipboard' }));
      }
   };

   return (
      <div className="py-4 space-y-6 border border-gray-300 rounded-md h-fit bg-grey-600">
         <div className="flex flex-col w-full px-4 space-y-3">
            {BankDetailsList?.map(({ title, value }, index) => (
               <div className="flex flex-col" key={index}>
                  <div className="flex flex-wrap items-center text-sm font-semibold gap-x-1">
                     {title}
                  </div>
                  <p className="flex items-center text-xs font-semibold text-grey-200 gap-x-2">
                     {value}
                     <CopyIcon
                        onClick={() => handleIsCopied(value)}
                        className="w-3 cursor-pointer"
                     />
                  </p>
               </div>
            ))}
         </div>
         <div className="relative border-t">
            <span className="absolute text-xs md:text-base z-9 bg-grey-600 -top-3.5 ltr:left-[40px] rtl:right-[70px] ltr:md:left-[60px] rtl:md:right-[100px] px-8 text-grey-200 font-semibold">
               <Translate id="wallet:for international" />
            </span>
         </div>
         <div className="flex flex-col w-full px-4 space-y-3">
            {BranchDetailsList?.map(({ title, value }, index) => (
               <div className="flex flex-col" key={index}>
                  <div className="flex flex-wrap items-center text-sm font-semibold gap-x-1">
                     {title}
                  </div>
                  <p className="flex items-center text-xs font-semibold text-grey-200 gap-x-2">
                     {value}
                     <span className="w-3">
                        <CopyIcon
                           onClick={() => handleIsCopied(value)}
                           className="w-3 cursor-pointer"
                        />
                     </span>
                  </p>
               </div>
            ))}
         </div>
      </div>
   );
};

const RequestDoneAlert = ({ closeModal }: { closeModal: () => void }) => {
   return (
      <div className="flex flex-col items-center space-y-4 text-center p-14">
         <button
            onClick={() => closeModal()}
            type="button"
            className="absolute z-10 bg-white rounded-full shadow-lg focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-primary top-2 ltr:right-2 rtl:left-2 ">
            <CloseIcon />
         </button>
         <WalletOutlineIcon width={40} height={40} className="mb-3 fill-primary" />
         <div className="my-2 text-2xl font-bold capitalize">
            <Translate id="wallet:recharge requested" />
         </div>
         <div className="text-lg font-semibold capitalize text-grey-100">
            <Translate id="wallet:recharge requested description" />
         </div>
      </div>
   );
};

export default ChargeWalletModal;
